import React, { useEffect, useMemo, useState } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import api from '../services/apiService';
import { useNotification } from '../components/NotificationProvider';
import { ACADEMY_ADMIN_API } from '../utils/academyAccess';
import { compareAcademyTeams } from '../utils/academyTeamSort';

const teamLabel = (team) => {
  const name = team.team_name || `Team ${team.team_id}`;
  return team.age_group ? `${name} · ${team.age_group}` : name;
};

const poundsFromPence = (pence) => (Number(pence || 0) / 100).toFixed(2);

const penceFromInput = (raw) => {
  const trimmed = String(raw ?? '').trim();
  if (trimmed === '') {
    return null;
  }
  const value = Number(trimmed);
  if (!Number.isFinite(value)) {
    return Number.NaN;
  }
  return Math.round(value * 100);
};

const hasSiblingDiscount = (player) => !player.is_free && Boolean(player.sibling_discount?.applied);

const savedAmount = (player) => (
  player.amount_pence > 0 ? poundsFromPence(player.amount_pence) : ''
);

const penceFromPounds = (pounds) => {
  const parsed = Math.round(Number(pounds) * 100);
  return parsed > 0 ? parsed : 0;
};

const inheritedPence = (player, defaultAmountPounds, teamAmounts) => {
  const fees = (player.teams || []).map((team) => {
    const typed = teamAmounts?.[team.team_id];
    if (typed !== undefined && String(typed).trim() !== '') {
      return penceFromPounds(typed);
    }
    if (team.amount_pence > 0) {
      return Number(team.amount_pence);
    }
    return penceFromPounds(defaultAmountPounds);
  }).filter((pence) => pence > 0);
  const unique = [...new Set(fees)];
  if (unique.length === 1) {
    return unique[0];
  }
  return player.default_amount_pence > 0 ? Number(player.default_amount_pence) : 0;
};

const AcademyPlayers = ({
  academyId,
  subscriptionsEnabled = true,
  defaultAmountPounds = '',
  teamAmounts = {},
}) => {
  const [players, setPlayers] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [billingFilter, setBillingFilter] = useState('all');
  const [savingId, setSavingId] = useState(0);
  const [savingPriceId, setSavingPriceId] = useState(0);
  const { showSuccess, showError } = useNotification();

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`${ACADEMY_ADMIN_API}/get_academy_players.php?id=${academyId}`);
      if (!response.data.success) {
        throw new Error(response.data.message || 'Could not load players');
      }
      setPlayers(response.data.players || []);
      setDrafts({});
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not load players');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [academyId]);

  const counts = useMemo(() => ({
    all: players.length,
    free: players.filter((player) => player.is_free).length,
    custom: players.filter((player) => !player.is_free && player.amount_pence > 0).length,
    sibling: players.filter(hasSiblingDiscount).length,
    paying: players.filter((player) => !player.is_free && !(player.amount_pence > 0)).length,
  }), [players]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return players
      .map((player) => ({
        ...player,
        teams: [...(player.teams || [])].sort(compareAcademyTeams),
      }))
      .filter((player) => {
        if (billingFilter === 'free' && !player.is_free) {
          return false;
        }
        if (billingFilter === 'custom' && (player.is_free || !(player.amount_pence > 0))) {
          return false;
        }
        if (billingFilter === 'sibling' && !hasSiblingDiscount(player)) {
          return false;
        }
        if (billingFilter === 'paying' && (player.is_free || player.amount_pence > 0)) {
          return false;
        }
        if (!term) {
          return true;
        }
        const teams = (player.teams || []).map(teamLabel).join(' ').toLowerCase();
        return `${player.name || ''} ${teams}`.toLowerCase().includes(term);
      });
  }, [players, search, billingFilter]);

  const amountValue = (player) => (
    Object.prototype.hasOwnProperty.call(drafts, player.player_id)
      ? drafts[player.player_id]
      : savedAmount(player)
  );

  const toggleFree = async (player) => {
    const nextFree = !player.is_free;
    setSavingId(player.player_id);
    setError('');
    try {
      const response = await api.post(`${ACADEMY_ADMIN_API}/update_academy_player_free.php`, {
        id: academyId,
        player_id: player.player_id,
        is_free: nextFree,
      });
      if (!response.data.success) {
        throw new Error(response.data.message || 'Could not update this player');
      }
      setPlayers((current) => current.map((row) => (
        row.player_id === player.player_id ? { ...row, is_free: nextFree } : row
      )));
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not update this player');
    } finally {
      setSavingId(0);
    }
  };

  const savePrice = async (player, rawValue) => {
    if (!subscriptionsEnabled || player.is_free || savingPriceId === player.player_id) {
      return;
    }
    const raw = rawValue === undefined ? amountValue(player) : rawValue;
    const nextPence = penceFromInput(raw);
    const currentPence = player.amount_pence > 0 ? Number(player.amount_pence) : null;
    if (Number.isNaN(nextPence)) {
      const message = 'Enter the player fee in pounds, or leave it blank to use the team fee.';
      setError(message);
      showError(message);
      return;
    }
    if (nextPence !== null && nextPence < 1) {
      const message = 'Enter an amount above zero, or leave it blank to use the team fee. Switch Free on if they should not be charged.';
      setError(message);
      showError(message);
      return;
    }
    if (nextPence === currentPence) {
      setDrafts((current) => {
        if (!Object.prototype.hasOwnProperty.call(current, player.player_id)) {
          return current;
        }
        const next = { ...current };
        delete next[player.player_id];
        return next;
      });
      return;
    }

    setSavingPriceId(player.player_id);
    setError('');
    try {
      const response = await api.post(`${ACADEMY_ADMIN_API}/update_academy_player_price.php`, {
        id: academyId,
        player_id: player.player_id,
        amount_pounds: nextPence === null ? '' : (nextPence / 100).toFixed(2),
      });
      if (!response.data.success) {
        throw new Error(response.data.message || 'Could not save this fee');
      }
      const savedPence = response.data.amount_pence > 0 ? Number(response.data.amount_pence) : null;
      setPlayers((current) => current.map((row) => (
        row.player_id === player.player_id ? { ...row, amount_pence: savedPence } : row
      )));
      setDrafts((current) => {
        const next = { ...current };
        delete next[player.player_id];
        return next;
      });
      showSuccess(
        savedPence
          ? `Saved £${(savedPence / 100).toFixed(2)} for ${player.name}.`
          : `Cleared the player fee for ${player.name}.`,
        3500
      );
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Could not save this fee';
      setError(message);
      showError(message);
    } finally {
      setSavingPriceId(0);
    }
  };

  if (loading) {
    return <p>Loading players…</p>;
  }

  return (
    <div className="settings-form">
      <h3 className="tab-title">Players</h3>
      {subscriptionsEnabled ? null : (
        <p className="stripe-notice" role="status">
          Stripe needs to be integrated before a player fee can be changed.
        </p>
      )}
      <p className="hint">
        Everyone on the teams assigned to this academy.
        {subscriptionsEnabled
          ? ' Leave the amount blank to charge the team fee. A bespoke amount replaces that team fee for this player. Sibling discount still applies. An existing Stripe subscription keeps the amount it was set up with. Making a player free does not cancel an existing Stripe subscription.'
          : ' Billing changes stay off until Stripe is connected.'}
      </p>
      {error ? <div className="alert alert-error">{error}</div> : null}
      {players.length > 0 ? (
        <div className="billing-filters" role="group" aria-label="Filter players">
          {[
            { id: 'all', label: 'All', count: counts.all },
            { id: 'custom', label: 'Bespoke', count: counts.custom },
            { id: 'sibling', label: 'Sibling discount', count: counts.sibling },
            { id: 'free', label: 'Free', count: counts.free },
            { id: 'paying', label: 'Full paying', count: counts.paying },
          ].map((filter) => (
            <button
              key={filter.id}
              type="button"
              className={billingFilter === filter.id ? 'active' : ''}
              aria-pressed={billingFilter === filter.id}
              disabled={!subscriptionsEnabled}
              onClick={() => setBillingFilter(filter.id)}
            >
              {filter.label}
              <span className="count">{filter.count}</span>
            </button>
          ))}
        </div>
      ) : null}
      <label className="field">
        Search
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Name or team"
        />
      </label>
      {players.length === 0 ? (
        <p className="hint">No players are on the teams assigned to this academy.</p>
      ) : visible.length === 0 ? (
        <p className="hint">
          {search.trim()
            ? 'No players match that search.'
            : billingFilter === 'free'
              ? 'No free players.'
              : billingFilter === 'custom'
                ? 'No players have a bespoke fee.'
                : billingFilter === 'sibling'
                  ? 'No players have a sibling discount.'
                  : 'No full paying players.'}
        </p>
      ) : (
        <ul className="player-list">
          {visible.map((player) => {
            const sibling = player.sibling_discount || {};
            const saving = savingId === player.player_id;
            const savingPrice = savingPriceId === player.player_id;
            const showFee = !player.is_free;
            const typedAmount = amountValue(player);
            const isSet = String(typedAmount).trim() !== '';
            const fromFees = inheritedPence(player, defaultAmountPounds, teamAmounts);
            const placeholder = !isSet && fromFees > 0 ? poundsFromPence(fromFees) : '';
            const showSibling = !player.is_free && sibling.applied;
            const showCustom = !player.is_free && player.amount_pence > 0;
            return (
              <li key={player.player_id}>
                <div className="player-main">
                  <strong>{player.name}</strong>
                  <small>{(player.teams || []).map(teamLabel).join(', ') || 'No team'}</small>
                  {showCustom || showSibling ? (
                    <span className="player-badges">
                      {showCustom ? (
                        <span className="player-badge is-custom">Bespoke fee</span>
                      ) : null}
                      {showSibling ? (
                        <span className="player-badge is-sibling">
                          Sibling discount · {sibling.label}
                          {sibling.parent_name ? ` · ${sibling.parent_name}` : ''}
                        </span>
                      ) : null}
                    </span>
                  ) : null}
                </div>
                <div className="player-actions">
                  {showFee ? (
                    <label className="field player-fee-field">
                      <span className="player-fee-label">
                        Player fee
                        <span
                          className="fee-info"
                          tabIndex={0}
                          onMouseDown={(event) => event.preventDefault()}
                        >
                          <FaInfoCircle aria-hidden="true" />
                          <span className="fee-info-tip" role="tooltip">
                            The fee entered here supersedes the fee set on the Fees tab.
                          </span>
                        </span>
                      </span>
                      <span className={`fee-input${isSet ? ' is-set' : ' is-inherited'}`}>
                        <span className="fee-prefix" aria-hidden="true">£</span>
                        {!isSet && placeholder ? (
                          <span className="fee-ghost" aria-hidden="true">{placeholder}</span>
                        ) : null}
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          inputMode="decimal"
                          value={typedAmount}
                          placeholder={placeholder}
                          disabled={!subscriptionsEnabled || savingPrice}
                          aria-label={`Player fee for ${player.name}`}
                          onChange={(event) => {
                            const next = event.target.value;
                            setDrafts((current) => ({ ...current, [player.player_id]: next }));
                          }}
                          onBlur={(event) => savePrice(player, event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              event.currentTarget.blur();
                            }
                          }}
                        />
                      </span>
                    </label>
                  ) : null}
                  <label className="player-free-field">
                    Free
                    <span className={`ios-switch${saving ? ' is-saving' : ''}`}>
                      <input
                        type="checkbox"
                        role="switch"
                        checked={Boolean(player.is_free)}
                        disabled={saving || !subscriptionsEnabled}
                        aria-label={`Free for ${player.name}`}
                        onChange={() => toggleFree(player)}
                      />
                      <span className="track" aria-hidden="true" />
                    </span>
                  </label>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default AcademyPlayers;
